import React, { useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Text,
  Title3,
  Body1,
  Body2,
  Caption1,
  ProgressBar,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ArrowUpload24Regular,
  DocumentData24Regular,
  Dismiss24Regular,
  ErrorCircle24Regular,
} from '@fluentui/react-icons';
import { uploadFile, UploadProgress } from '../../api/uploadService';
import { formatFileSize } from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

const ACCEPTED_EXTENSIONS: Record<string, string[]> = {
  excel: ['.xlsx', '.xls', '.xlsm'],
  csv: ['.csv'],
  // Virtual 'file' connector accepts both CSV and Excel
  file: ['.csv', '.xlsx', '.xls', '.xlsm'],
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface UploadViewProps {
  onUploadComplete: (connectionId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UploadView: React.FC<UploadViewProps> = ({ onUploadComplete }) => {
  const [searchParams] = useSearchParams();
  const connectorId = searchParams.get('connector') || 'csv';

  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const acceptedTypes = connectorId === 'file'
    ? '.csv,.xlsx,.xls,.xlsm'
    : connectorId === 'excel'
      ? '.xlsx,.xls,.xlsm'
      : '.csv';

  const acceptedLabel = connectorId === 'file'
    ? 'CSV or Excel files (.csv, .xlsx, .xls)'
    : connectorId === 'excel'
      ? 'Excel files (.xlsx, .xls, .xlsm)'
      : 'CSV files (.csv)';

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const validateFile = useCallback((file: File): string | null => {
    // Validate extension
    const extensions = ACCEPTED_EXTENSIONS[connectorId] || ACCEPTED_EXTENSIONS.csv;
    const fileName = file.name.toLowerCase();
    const hasValidExt = extensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExt) {
      return `Invalid file type. Accepted: ${extensions.join(', ')}`;
    }
    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`;
    }
    return null;
  }, [connectorId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const error = validateFile(files[0]);
      if (error) {
        setUploadError(error);
        return;
      }
      setSelectedFile(files[0]);
      setUploadError(null);
    }
  }, [validateFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const error = validateFile(files[0]);
      if (error) {
        setUploadError(error);
        // Reset input so the same file can be re-selected after fixing
        e.target.value = '';
        return;
      }
      setSelectedFile(files[0]);
      setUploadError(null);
    }
  }, [validateFile]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(null);

    const controller = new AbortController();
    abortRef.current = controller;

    // When using the unified 'file' connector, resolve the actual connector
    // from the file extension so the backend creates the correct connection type
    let resolvedConnectorId = connectorId;
    if (connectorId === 'file') {
      const ext = selectedFile.name.toLowerCase();
      resolvedConnectorId = (ext.endsWith('.xlsx') || ext.endsWith('.xls') || ext.endsWith('.xlsm'))
        ? 'excel'
        : 'csv';
    }

    try {
      const result = await uploadFile(
        selectedFile,
        (progress) => setUploadProgress(progress),
        resolvedConnectorId,
        controller.signal
      );

      onUploadComplete(result.id);
    } catch (err) {
      if (controller.signal.aborted) return; // User cancelled — don't show error
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
    }
  }, [selectedFile, connectorId, onUploadComplete]);

  const handleCancelUpload = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(null);
    setUploadError(null);
  }, []);

  return (
    <div className="upload-view">
      <div className="upload-header">
        <Title3>Upload Data File</Title3>
        <Body1>
          Upload a {connectorId === 'file' ? 'CSV or Excel file' : connectorId === 'excel' ? 'spreadsheet' : 'CSV file'} to create a new data source.
          Your data will be ingested, profiled, and made available for dashboards.
        </Body1>
      </div>

      {/* Drop zone */}
      <div
        className={mergeClasses(
          'drop-zone',
          isDragOver && 'drop-zone-active',
          !!(selectedFile && !isUploading) && 'drop-zone-has-file'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!selectedFile ? handleBrowseClick : undefined}
        onKeyDown={!selectedFile ? handleKeyDown : undefined}
        role="button"
        tabIndex={0}
        aria-label={selectedFile ? `Selected file: ${selectedFile.name}` : 'Drop file here or press Enter to browse'}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {!selectedFile ? (
          <>
            <ArrowUpload24Regular className="drop-zone-icon" />
            <Title3>Drop your file here</Title3>
            <Body1>or click to browse</Body1>
            <Caption1 className="drop-zone-hint">
              Accepts {acceptedLabel} • Max 500MB
            </Caption1>
          </>
        ) : (
          <div className="file-info">
            <DocumentData24Regular className="file-icon" />
            <div className="file-details">
              <Text weight="semibold">{selectedFile.name}</Text>
              <Caption1>{formatFileSize(selectedFile.size)}</Caption1>
            </div>
            {!isUploading && (
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  setUploadError(null);
                }}
                title="Remove file"
              />
            )}
          </div>
        )}
      </div>

      {/* Upload progress */}
      {isUploading && uploadProgress && (
        <div className="upload-progress">
          <div className="upload-progress-header">
            <Body2>Uploading...</Body2>
            <Body2>{uploadProgress.percentage}%</Body2>
          </div>
          <ProgressBar
            value={uploadProgress.percentage / 100}
            thickness="large"
          />
          <Caption1>
            {formatFileSize(uploadProgress.loaded)} / {formatFileSize(uploadProgress.total)}
          </Caption1>
        </div>
      )}

      {/* Error */}
      {uploadError && (
        <div className="upload-error">
          <ErrorCircle24Regular />
          <Body1>{uploadError}</Body1>
        </div>
      )}

      {/* Actions */}
      <div className="upload-actions">
        {isUploading ? (
          <Button
            appearance="secondary"
            size="large"
            onClick={handleCancelUpload}
          >
            Cancel
          </Button>
        ) : (
          <Button
            appearance="primary"
            size="large"
            disabled={!selectedFile}
            onClick={handleUpload}
            icon={<ArrowUpload24Regular />}
          >
            Upload File
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadView;